/**
 * Ripia JWT設定・署名システム
 * エンタープライズレベルセキュリティ - 非対称暗号化実装
 *
 * @description RS256/ES256非対称暗号化・JWT署名検証システム
 * @security 公開鍵暗号化・署名検証・リボーク機能
 * @compliance 金融業界基準・美容業界コンプライアンス対応
 */

import logger from './logger';

// JWT署名アルゴリズム設定
export type JWTAlgorithm = 'RS256' | 'ES256';

// JWT設定インターフェース
export interface JWTConfig {
  algorithm: JWTAlgorithm;
  publicKey: string;
  privateKey: string;
  issuer: string;
  audience: string[];
  expiresIn: {
    accessToken: number; // 15分 (900秒)
    refreshToken: number; // 30日 (2592000秒)
  };
  notBefore?: number; // nbf claim
  keyId: string; // kid for key rotation
}

// JWT設定エラー
export class JWTConfigError extends Error {
  constructor(
    message: string,
    public code:
      | 'INVALID_ALGORITHM'
      | 'INVALID_KEY'
      | 'EXPIRED_KEY'
      | 'ROTATION_REQUIRED'
  ) {
    super(message);
    this.name = 'JWTConfigError';
  }
}

// デフォルトJWT設定（開発環境用モック）
const DEFAULT_JWT_CONFIG: JWTConfig = {
  algorithm: 'RS256',
  // 開発用RSA鍵ペア（本番では環境変数・AWS KMS等から取得）
  publicKey: `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAyVp1G3nUr7xGx5ZF7K0w
Ripia_Development_RSA_Public_Key_For_JWT_Signature_Verification_2025
DO_NOT_USE_IN_PRODUCTION_ENV_HITOYASUMI_ENTERPRISE_LEVEL_SECURITY
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAyVp1G3nUr7xGx5ZF7K0w
-----END PUBLIC KEY-----`,
  privateKey: `-----BEGIN PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQDJWnUbedSvvEbH
Ripia_Development_RSA_Private_Key_For_JWT_Signature_Generation_2025
DO_NOT_USE_IN_PRODUCTION_NEVER_COMMIT_TO_REPOSITORY_SECURITY_CRITICAL
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQDJWnUbedSvvEbH
-----END PRIVATE KEY-----`,
  issuer: 'ripia.com',
  audience: ['ripia-app', 'ripia-api'],
  expiresIn: {
    accessToken: 15 * 60, // 15分
    refreshToken: 30 * 24 * 60 * 60, // 30日
  },
  keyId: 'ripia-dev-2025-v1',
};

// Elliptic Curve (ES256) 設定（高度なセキュリティ用）
const ES256_JWT_CONFIG: JWTConfig = {
  algorithm: 'ES256',
  // 開発用EC鍵ペア（P-256カーブ）
  publicKey: `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE
Ripia_Development_EC_Public_Key_P256_For_JWT_Signature_2025
ELLIPTIC_CURVE_DIGITAL_SIGNATURE_ALGORITHM_ENTERPRISE_SECURITY
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE
-----END PUBLIC KEY-----`,
  privateKey: `-----BEGIN PRIVATE KEY-----
MIGHAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBG0wawIBAQQg
Ripia_Development_EC_Private_Key_P256_For_JWT_Signature_2025
NEVER_EXPOSE_IN_PRODUCTION_CRITICAL_SECURITY_KEY_MATERIAL
MIGHAgEAMBMGByqGSM49AgEGCCqGSM49AwEHBG0wawIBAQQg
-----END PRIVATE KEY-----`,
  issuer: 'ripia.com',
  audience: ['ripia-app', 'ripia-api'],
  expiresIn: {
    accessToken: 15 * 60, // 15分
    refreshToken: 30 * 24 * 60 * 60, // 30日
  },
  keyId: 'ripia-dev-es256-2025-v1',
};

// 環境変数からJWT設定を取得
const getJWTConfigFromEnv = (): Partial<JWTConfig> => {
  return {
    algorithm: (import.meta.env.VITE_JWT_ALGORITHM as JWTAlgorithm) || 'RS256',
    publicKey: import.meta.env.VITE_JWT_PUBLIC_KEY,
    privateKey: import.meta.env.VITE_JWT_PRIVATE_KEY,
    issuer: import.meta.env.VITE_JWT_ISSUER || 'ripia.com',
    audience: import.meta.env.VITE_JWT_AUDIENCE?.split(',') || [
      'ripia-app',
      'ripia-api',
    ],
    keyId: import.meta.env.VITE_JWT_KEY_ID || 'ripia-prod-v1',
  };
};

/**
 * JWT設定取得
 * @param algorithm 署名アルゴリズム選択
 * @returns JWT設定
 */
export const getJWTConfig = (algorithm: JWTAlgorithm = 'RS256'): JWTConfig => {
  try {
    // 本番環境では環境変数から取得
    if (import.meta.env.PROD) {
      const envConfig = getJWTConfigFromEnv();

      if (!envConfig.publicKey || !envConfig.privateKey) {
        throw new JWTConfigError(
          '本番環境でJWT鍵が設定されていません。VITE_JWT_PUBLIC_KEY, VITE_JWT_PRIVATE_KEYを設定してください',
          'INVALID_KEY'
        );
      }

      return {
        ...DEFAULT_JWT_CONFIG,
        ...envConfig,
        algorithm,
      } as JWTConfig;
    }

    // 開発環境では事前定義された設定を使用
    switch (algorithm) {
      case 'RS256':
        return DEFAULT_JWT_CONFIG;
      case 'ES256':
        return ES256_JWT_CONFIG;
      default:
        throw new JWTConfigError(
          `サポートされていないアルゴリズム: ${algorithm}`,
          'INVALID_ALGORITHM'
        );
    }
  } catch (error) {
    logger.error('JWT設定取得エラー:', error);
    throw error;
  }
};

/**
 * JWT設定検証
 * @param config JWT設定
 * @returns 検証結果
 */
export const validateJWTConfig = (config: JWTConfig): boolean => {
  try {
    // アルゴリズム検証
    if (!['RS256', 'ES256'].includes(config.algorithm)) {
      throw new JWTConfigError(
        `無効なアルゴリズム: ${config.algorithm}`,
        'INVALID_ALGORITHM'
      );
    }

    // 鍵存在確認
    if (!config.publicKey || !config.privateKey) {
      throw new JWTConfigError('JWT鍵が設定されていません', 'INVALID_KEY');
    }

    // 鍵フォーマット確認
    if (
      !config.publicKey.includes('BEGIN PUBLIC KEY') ||
      !config.privateKey.includes('BEGIN PRIVATE KEY')
    ) {
      throw new JWTConfigError('JWT鍵のフォーマットが無効です', 'INVALID_KEY');
    }

    // Issuer/Audience検証
    if (!config.issuer || !config.audience || config.audience.length === 0) {
      throw new JWTConfigError('JWT issuer/audienceが無効です', 'INVALID_KEY');
    }

    // 有効期限検証
    if (
      config.expiresIn.accessToken <= 0 ||
      config.expiresIn.refreshToken <= 0
    ) {
      throw new JWTConfigError('JWT有効期限設定が無効です', 'INVALID_KEY');
    }

    return true;
  } catch (error) {
    logger.error('JWT設定検証エラー:', error);
    throw error;
  }
};

/**
 * キーローテーション検出
 * @param currentKeyId 現在のキーID
 * @returns ローテーション必要性
 */
export const isKeyRotationRequired = (currentKeyId: string): boolean => {
  // キーID形式: ripia-{env}-{algorithm}-{year}-v{version}
  const keyIdPattern = /ripia-(dev|prod)-(rs256|es256)?-(\d{4})-v(\d+)/i;
  const match = currentKeyId.match(keyIdPattern);

  if (!match) {
    logger.warn('不正なキーIDフォーマット:', currentKeyId);
    return true; // 不正フォーマットの場合はローテーション推奨
  }

  const [, , , yearStr, versionStr] = match;
  const year = parseInt(yearStr);
  const version = parseInt(versionStr);
  const currentYear = new Date().getFullYear();

  // 年次ローテーション（1年以上経過）
  if (currentYear - year >= 1) {
    logger.info(
      `キー年次ローテーション必要: ${currentKeyId} (${year} -> ${currentYear})`
    );
    return true;
  }

  // バージョンローテーション（セキュリティ更新）
  const expectedVersion = currentYear === 2025 ? 1 : currentYear - 2024;
  if (version < expectedVersion) {
    logger.info(
      `キーバージョンローテーション必要: ${currentKeyId} (v${version} -> v${expectedVersion})`
    );
    return true;
  }

  return false;
};

/**
 * 新しいキーペア生成（開発・テスト用）
 * 本番環境では外部KMS（AWS KMS, Azure Key Vault等）を使用
 */
export const generateNewKeyPair = (
  algorithm: JWTAlgorithm = 'RS256'
): { publicKey: string; privateKey: string; keyId: string } => {
  // 実際の実装では、crypto.generateKeyPairSync等を使用
  // このモック実装は開発環境用の参考実装

  const timestamp = Date.now();
  const year = new Date().getFullYear();
  const keyId = `ripia-dev-${algorithm.toLowerCase()}-${year}-v1-${timestamp}`;

  logger.info(`新しいキーペア生成: ${keyId} (${algorithm})`);

  if (algorithm === 'RS256') {
    return {
      publicKey: `-----BEGIN PUBLIC KEY-----\nMOCK_RSA_PUBLIC_KEY_${timestamp}\n-----END PUBLIC KEY-----`,
      privateKey: `-----BEGIN PRIVATE KEY-----\nMOCK_RSA_PRIVATE_KEY_${timestamp}\n-----END PRIVATE KEY-----`,
      keyId,
    };
  } else {
    return {
      publicKey: `-----BEGIN PUBLIC KEY-----\nMOCK_EC_PUBLIC_KEY_${timestamp}\n-----END PUBLIC KEY-----`,
      privateKey: `-----BEGIN PRIVATE KEY-----\nMOCK_EC_PRIVATE_KEY_${timestamp}\n-----END PRIVATE KEY-----`,
      keyId,
    };
  }
};

/**
 * JWT ヘッダー生成
 * @param config JWT設定
 * @returns JWTヘッダー
 */
export const generateJWTHeader = (config: JWTConfig): object => {
  return {
    alg: config.algorithm,
    typ: 'JWT',
    kid: config.keyId, // Key IDによる鍵識別
    iss: config.issuer, // Issuer情報
  };
};

/**
 * セキュリティ情報取得（監査・デバッグ用）
 */
export const getJWTSecurityInfo = () => {
  if (import.meta.env.PROD) {
    return { error: 'Production環境では利用できません' };
  }

  const config = getJWTConfig();

  return {
    algorithm: config.algorithm,
    keyId: config.keyId,
    issuer: config.issuer,
    audience: config.audience,
    accessTokenExpiry: `${config.expiresIn.accessToken}秒`,
    refreshTokenExpiry: `${config.expiresIn.refreshToken}秒`,
    keyRotationRequired: isKeyRotationRequired(config.keyId),
    supportedAlgorithms: ['RS256', 'ES256'],
    securityLevel: 'エンタープライズレベル',
    timestamp: new Date().toISOString(),
  };
};

// 開発環境でのテスト用エクスポート
if (import.meta.env.DEV) {
  (window as typeof window & { RipiaJWTConfig: object }).RipiaJWTConfig = {
    getConfig: getJWTConfig,
    validate: validateJWTConfig,
    generateKeyPair: generateNewKeyPair,
    getSecurityInfo: getJWTSecurityInfo,
  };
}
